import { Component, computed, inject, signal } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { UpperCasePipe } from '@angular/common';
import { filter } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../auth/auth.service';
import { NavigationService, NavItem, NavGroup } from '../../core/services/navigation.service';
import { NAV_GROUP_ICONS } from '../../core/services/nav-groups';

const RAIL_KEY = 'zf.railExpanded';
const RAIL_COLLAPSED = 92;
const RAIL_EXPANDED = 250;

interface Flyout {
  group: NavGroup;
  top: number;
  maxHeight: number;
}

/**
 * Dark rail sidebar. Two modes:
 *  - collapsed (92px): icon + short label per group; hovering a group opens a
 *    flyout panel listing its modules and pages;
 *  - expanded (250px): groups behave as an accordion with inline pages.
 * The choice is remembered per browser. The rail publishes its width through
 * the `--zf-rail-w` custom property so the top bar and content shift with it.
 */
@Component({
  selector: 'app-sidemenu',
  standalone: true,
  imports: [RouterModule, UpperCasePipe],
  templateUrl: './sidemenu.component.html',
  styleUrl: './sidemenu.component.scss',
})
export class SidemenuComponent {
  private authService = inject(AuthService);
  private router = inject(Router);
  private navService = inject(NavigationService);

  expanded = signal(localStorage.getItem(RAIL_KEY) === '1');
  openGroup = signal<string | null>(null);
  flyout = signal<Flyout | null>(null);

  private user = toSignal(this.authService.user$, { initialValue: null as any });
  logoUrl = computed(() => this.user()?.preferences?.logoUrl || null);

  /** Re-evaluated on every navigation so RAC/role changes are reflected. */
  private navTick = signal(0);
  groups = computed(() => {
    this.navTick();
    return this.navService.getVisibleGroups();
  });

  railWidth = computed(() => (this.expanded() ? RAIL_EXPANDED : RAIL_COLLAPSED));

  private closeTimer: any = null;

  constructor() {
    this.applyRailWidth();
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.navTick.update((n) => n + 1);
        this.syncOpenGroup();
        this.flyout.set(null);
      });
    this.syncOpenGroup();
  }

  iconPath(name: string): string {
    return NAV_GROUP_ICONS[name] ?? NAV_GROUP_ICONS['products'];
  }

  isActiveGroup(groupId: string): boolean {
    return this.navService.getActiveGroup()?.id === groupId;
  }

  isActiveItem(item: NavItem): boolean {
    return this.navService.isItemActive(item);
  }

  toggle(): void {
    const next = !this.expanded();
    this.expanded.set(next);
    this.flyout.set(null);
    localStorage.setItem(RAIL_KEY, next ? '1' : '0');
    this.applyRailWidth();
    if (next) this.syncOpenGroup();
  }

  onClickGroup(groupId: string, event: MouseEvent): void {
    if (this.expanded()) {
      this.openGroup.update((cur) => (cur === groupId ? null : groupId));
      return;
    }
    // Collapsed: a click behaves like hover (touch devices have no hover).
    this.onEnterGroup(groupId, event);
  }

  onEnterGroup(groupId: string, event: MouseEvent): void {
    if (this.expanded()) return;
    this.cancelClose();
    const group = this.groups().find((g) => g.id === groupId);
    if (!group) return;
    const target = (event.currentTarget as HTMLElement) ?? (event.target as HTMLElement);
    const rect = target.getBoundingClientRect();
    const sections = group.categories.length > 1 ? group.categories.length : 0;
    const leaves = group.categories.reduce((n, c) => n + c.items.length, 0);
    const est = 60 + sections * 38 + leaves * 31;
    const vh = window.innerHeight;
    const maxHeight = Math.min(est, vh - 24);
    const top = Math.min(Math.max(8, rect.top - 4), Math.max(8, vh - maxHeight - 12));
    this.flyout.set({ group, top, maxHeight });
  }

  holdFlyout(): void {
    this.cancelClose();
  }

  onLeaveRail(): void {
    this.cancelClose();
    this.closeTimer = setTimeout(() => this.flyout.set(null), 240);
  }

  closeFlyout(): void {
    this.cancelClose();
    this.flyout.set(null);
  }

  logout() {
    this.authService.logout();
    this.router.navigateByUrl('');
  }

  private cancelClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private syncOpenGroup(): void {
    const active = this.navService.getActiveGroup();
    if (active) this.openGroup.set(active.id);
  }

  private applyRailWidth(): void {
    document.documentElement.style.setProperty('--zf-rail-w', `${this.railWidth()}px`);
  }
}
